import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UruannaMark, type MarkTone } from '@/components/brand/UruannaMark';

/**
 * The compact mark is the spinner: its two bursts counter-rotate (1.8s, eased
 * so they pause as they line up). For a button's busy state, a typeahead's
 * search, an upload — anything working. `inverse` on an indigo fill.
 */
export function Spinner({
  size = 20,
  tone = 'brand',
  label,
}: {
  size?: number;
  tone?: MarkTone;
  /** Announced name; omit when the surrounding control already says it is busy. */
  label?: string;
}) {
  return (
    <Box
      component="span"
      role={label ? 'progressbar' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0 }}
    >
      <UruannaMark size={size} tone={tone} animate="spin" decorative />
    </Box>
  );
}

/**
 * A page — a lazy route, a first fetch with nothing to show — waiting. The
 * mark breathes rather than spins: it is waiting, not working.
 */
export function PageLoader({ label, fullScreen }: { label?: string; fullScreen?: boolean }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.75,
        py: fullScreen ? 0 : 10,
        minHeight: fullScreen ? '100dvh' : undefined,
      }}
    >
      <UruannaMark size={44} animate="breathe" decorative />
      <Typography role="status" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
        {label ?? t('common.loading')}
      </Typography>
    </Box>
  );
}

/** One column of a skeleton row: what kind of cell it stands in for. */
export type SkeletonCell = 'avatarText' | 'text' | 'chip' | 'number' | 'action';

/**
 * Skeleton rows for a table that is not the DataGrid (Loaders board): the
 * same columns as the real rows, 56px tall, so nothing jumps when the data
 * lands. They pulse (the theme's `MuiSkeleton`), they do not shimmer. The
 * DataGrid draws its own from its column types; this is for hand-built
 * lists.
 */
export function SkeletonRows({
  columns,
  rows = 3,
  template,
}: {
  columns: SkeletonCell[];
  rows?: number;
  /** A CSS grid template matching the real rows; equal columns otherwise. */
  template?: string;
}) {
  const widths = ['60%', '45%', '52%', '70%', '38%'];
  return (
    <Box role="presentation" aria-hidden>
      {Array.from({ length: rows }, (_, row) => (
        <Box
          key={row}
          sx={{
            display: 'grid',
            gridTemplateColumns: template ?? `repeat(${columns.length}, minmax(0, 1fr))`,
            gap: 2,
            alignItems: 'center',
            px: 2,
            height: 56,
            borderBottom: row === rows - 1 ? 0 : 1,
            borderColor: 'divider',
          }}
        >
          {columns.map((cell, index) => {
            const width = widths[(row + index) % widths.length];
            if (cell === 'avatarText')
              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Skeleton variant="circular" width={32} height={32} sx={{ flexShrink: 0 }} />
                  <Skeleton variant="text" width={width} />
                </Box>
              );
            if (cell === 'chip') return <Skeleton key={index} variant="rounded" width="40%" height={18} />;
            if (cell === 'action') return <Skeleton key={index} variant="rounded" width={28} height={18} />;
            return <Skeleton key={index} variant="text" width={cell === 'number' ? '35%' : width} />;
          })}
        </Box>
      ))}
    </Box>
  );
}
